import {CoinsHistoryTransaction} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coins_history_transaction.types';
import {CoinsSections_History} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wallet/v1/page/sections.types';
import {useQuantityFormat} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  CoinsRowTile,
  GenericIconName,
  Icon,
  SPACING,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useCoinsHistoryOpenedState} from '../../CoinsHistory/hooks/useCoinsHistoryOpenedState';
import {useIsMissionsActive} from '../../utils/useIsMissionsActive';

export const CoinsHistory: React.FC<CoinsSections_History> = ({
  title,
  description,
  transaction,
  ctaLabel,
}) => {
  const isActive = useIsMissionsActive();
  const {open} = useCoinsHistoryOpenedState();

  return (
    <Wrapper data-show-separator={isActive}>
      <Section data-testid="coinsWalletHistory">
        <TitleWrapper>
          <Text size="px16" weight="mediumHeavy">
            {title}
          </Text>
          {description && (
            <Text color="Grey0" size="px12" weight="mediumBold">
              {description}
            </Text>
          )}
        </TitleWrapper>
        <TransactionWrapper>
          <TransactionPreviewRow transaction={transaction} />
        </TransactionWrapper>
        <CTAWrapper>
          <CTAButton
            variant="secondary"
            onClick={() => open()}
            aria-label={undefined}
            data-testid="coinsWalletHistoryCTA"
          >
            {ctaLabel}
          </CTAButton>
        </CTAWrapper>
      </Section>
    </Wrapper>
  );
};

/**
 * Display-only transaction preview for the wallet's Coins History section.
 * Uses the shared CoinsRowTile.
 *
 * The full-history page keeps using the existing `Transaction` component
 * with its richer per-event layout; only the wallet preview is reshaped
 * onto the shared tile.
 */
const TransactionPreviewRow: React.FC<{
  transaction: CoinsHistoryTransaction;
}> = ({transaction}) => {
  const format = useQuantityFormat();
  const firstEvent = transaction.events[0];
  const eventType = firstEvent?.type;
  const getEventDetail = () => {
    if (eventType?.$case === 'confirmed') return eventType.confirmed;
    if (eventType?.$case === 'pending') return eventType.pending;
    return undefined;
  };
  const eventDetail = getEventDetail();

  const description = eventDetail?.description ?? transaction.source;
  const isPositive =
    eventDetail !== undefined &&
    eventType?.$case === 'confirmed' &&
    eventDetail.balanceChange > 0;

  return (
    <CoinsRowTile
      data-testid="coinsHistoryTransactionPreview"
      left={
        <IconWrapper>
          <Icon
            asset={GenericIconName.CoinsDoubleColor}
            size="SS"
            fill={COLORS['Fill.Grey.Secondary']}
          />
        </IconWrapper>
      }
      title={description}
      subtitle={transaction.date}
      right={
        eventDetail ? (
          <BalanceChange
            size="px12"
            weight="mediumHeavy"
            data-is-positive={isPositive}
          >
            {eventDetail.balanceChange > 0 ? '+' : ''}
            {format(eventDetail.balanceChange)}
          </BalanceChange>
        ) : null
      }
    />
  );
};

const Wrapper = styled.div`
  position: relative;

  &[data-show-separator='true'] {
    &:before {
      content: '';
      position: absolute;
      left: -${SPACING['X.Lg']};
      top: -2px;
      width: calc(100% + ${SPACING['X.Lg']} * 2);
      height: ${SPACING['Y.Md']};
      background-color: ${COLORS['Fill.Grey.Primary']};
    }
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${SPACING['Y.Xl']} 0 ${SPACING['Y.Lg']};
  gap: 4px;
`;

const TransactionWrapper = styled.div`
  padding: ${SPACING['Y.Xl']} 0;
`;

const CTAWrapper = styled.div`
  padding: 0 0 ${SPACING['Y.Xl']};
`;

const CTAButton = styled(Button)`
  &&& {
    width: 100%;
  }
`;

const IconWrapper = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${COLORS['Fill.Grey.Primary']};
`;

const BalanceChange = styled(Text)`
  padding: 0 6px;
  border-radius: 16px;
  line-height: 1.5;

  &[data-is-positive='true'] {
    background-color: ${COLORS['Alerts.Success.Tertiary']};
    color: ${COLORS['Alerts.Success.Primary']};
  }
`;
